import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { formatDate } from "../utils/dateUtils";
import { getSalesDocuments } from "../redux/actions/salesDocumentActions";
import { fetchFiles } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { listSalesPayments } from "../redux/actions/salesPaymentActions";
import SalesDocumentStats from "../components/SalesDocumentStats";
import SalesPaymentPopup from "../components/SalesPaymentPopup";
import { IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Calendar from "@mui/icons-material/Event";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";

import SendIcon from "@mui/icons-material/Send";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { FormControl } from "@mui/material";
import FileAutocomplete from "../components/FileAutocomplete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Avatar from "@mui/material/Avatar";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  TableSortLabel,
  Button,
  Container,
  TextField,
  Autocomplete,
  Grid,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Breadcrumbs,
  Typography,
  InputAdornment,
  Link,
  Stack,
  Tabs,
  Tab,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import axios from "../utils/axiosConfig";

import { saveAs } from "file-saver";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { actionButtonStyles } from "../styles/actionButton";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallBold, typoBase } from "../styles/typography";
const tableStyles = windowsTableStyles(); // Correctly invoke the function

const buttonStyles = {
  borderRadius: 1,
  padding: "6px 16px",
  textTransform: "none",
  boxShadow: "none",
  height: "fit-content",
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
  },
};

const tableHeaderStyles = {
  backgroundColor: "#f5f5f5",
  color: "#333",
  fontWeight: "bold",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  padding: "10px 15px",
};

const FinancePage = () => {
  const { t, i18n } = useTranslation(['pages', 'common', 'buttons']);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContractType, setSelectedContractType] = useState("");
  const [billingPeriod, setBillingPeriod] = useState([null, null]);

  const navigate = useNavigate();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const dispatch = useDispatch();
  const { salesDocuments } = useSelector((state) => state.salesDocumentList);
  const { salesPayments } = useSelector((state) => state.salesPaymentList);
  const { files } = useSelector((state) => state.fileList);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [caseFilter, setCaseFilter] = useState(null);
  const [fromFilter, setFromFilter] = useState(null);
  const [fromFilterCleared, setFromFilterCleared] = useState(false);
  const [toFilter, setToFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [open, setOpen] = useState(false);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt"); // Default sort by creation date/time

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const location = useLocation();
  
  // Always fetch the full sales documents list when visiting the Finance page.
  // This prevents case-specific views (which may load documents for a single case)
  // from leaving the global list in a filtered state when navigating back here.
  useEffect(() => {
    // Fetch the complete invoices list for the Finance overview
    dispatch(getSalesDocuments());

    // Ensure supporting data is loaded (files, relationships) without refetching unnecessarily
    if (!files || files.length === 0) {
      dispatch(fetchFiles());
    }
    if (!relationships || relationships.length === 0) {
      dispatch(fetchRelationships());
    }

    // Sales payments are part of the Finance overview and can be refreshed on each visit
    dispatch(listSalesPayments());
  // We intentionally depend only on dispatch so this runs once per mount.
  }, [dispatch]);

  // Auto-populate filter for all users (including Managers)
  useEffect(() => {
    if (user && relationships?.length > 0 && !fromFilter && !fromFilterCleared) {
      // Find the user's relationship in the relationships array
      const userRelationship = relationships?.find(
        (rel) => rel._id === user.relationship && rel.type === "Lawyer"
      );
      if (userRelationship) {
        setFromFilter(userRelationship);
      }
    }
  }, [user, relationships, fromFilter, fromFilterCleared]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return text?.substring(0, maxLength) + "...";
  };

  const userFiles = files?.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return Array.isArray(file.officeMembers) && file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user.role === "Assistant Lawyer") {
      return Array.isArray(file.officeMembers) && file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  // Calculate totals for invoices
  const calculateTotalDue = (documents) => {
    return (documents || []).reduce((sum, doc) => sum + (parseFloat(doc.totalDue) || 0), 0).toFixed(2);
  };

  const calculateTotalAmount = (documents) => {
    return (documents || []).reduce((sum, doc) => sum + (parseFloat(doc.total) || 0), 0).toFixed(2);
  };

  const filteredSalesDocuments = salesDocuments.filter((doc) => {
    const normalizeString = (str) => str.replace(/\s+/g, "").toLowerCase();

    const matchesSearchTerm = searchTerm
      ? doc.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.from &&
        normalizeString(
          `${doc.from?.firstName || ""} ${doc.from?.middleName || ""} ${doc.from?.lastName || ""
          }`
        ).includes(normalizeString(searchTerm))) ||
      (doc.to &&
        normalizeString(
          `${doc.to?.firstName || doc.to?.businessName} ${doc.to?.middleName || ""
          } ${doc.to?.lastName || ""}`
        ).includes(normalizeString(searchTerm))) ||
      (doc.case &&
        doc.case?.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      doc.case?.fileNo.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const matchesCaseFilter = caseFilter
      ? doc.case?._id === caseFilter?._id
      : true;
    const matchesFromFilter = fromFilter
      ? doc.from?._id === fromFilter._id
      : true;
    const matchesToFilter = toFilter ? doc.to?._id === toFilter._id : true;
    const matchesStatusFilter = statusFilter
      ? doc.status === statusFilter
      : true;
    const matchesBillingPeriod =
      billingPeriod[0] && billingPeriod[1]
        ? new Date(doc.dueDate) >= billingPeriod[0] &&
        new Date(doc.dueDate) <= billingPeriod[1]
        : true;

    // Check if user has access to the case file
    const hasFileAccess = userFiles?.some((file) => file._id === doc.case?._id);
    
    // For managers or when from filter is cleared, show all documents regardless of case ownership
    const shouldShowByAccessRules = 
      (user.role === "Manager" || fromFilterCleared) || hasFileAccess;
    
    if (!shouldShowByAccessRules) {
      return false;
    }

    return (
      matchesSearchTerm &&
      matchesCaseFilter &&
      matchesFromFilter &&
      matchesToFilter &&
      matchesStatusFilter &&
      matchesBillingPeriod
    );
  });

  const filteredSalesPayments = salesPayments.filter((payment) => {
    const normalizeString = (str) => str.replace(/\s+/g, "").toLowerCase();

    const matchesSearchTerm = searchTerm
      ? payment.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.from &&
        normalizeString(
          `${payment.from?.firstName || ""} ${payment.from?.middleName || ""
          } ${payment.from?.lastName || ""}`
        ).includes(normalizeString(searchTerm))) ||
      (payment.to &&
        normalizeString(
          `${payment.to?.firstName || payment.to?.businessName} ${payment.to?.middleName || ""
          } ${payment.to?.lastName || ""}`
        ).includes(normalizeString(searchTerm))) ||
      (payment.case &&
        payment.case?.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      payment.case?.fileNo.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const matchesCaseFilter = caseFilter
      ? payment.case?._id === caseFilter?._id
      : true;
    const matchesFromFilter = fromFilter
      ? payment.from?._id === fromFilter._id
      : true;
    const matchesToFilter = toFilter ? payment.to._id === toFilter._id : true;
    const matchesBillingPeriod =
      billingPeriod[0] && billingPeriod[1]
        ? new Date(payment.date) >= billingPeriod[0] &&
        new Date(payment.date) <= billingPeriod[1]
        : true;

    // Check if user has access to the case file
    const hasFileAccess = userFiles.some((file) => file._id === payment.case?._id);
    
    // For managers or when from filter is cleared, show all payments regardless of case ownership
    const shouldShowByAccessRules = 
      (user.role === "Manager" || fromFilterCleared) || hasFileAccess;
    
    if (!shouldShowByAccessRules) {
      return false;
    }

    return (
      matchesSearchTerm &&
      matchesCaseFilter &&
      matchesFromFilter &&
      matchesToFilter &&
      matchesBillingPeriod
    );
  });

  const handleViewDocument = async (id) => {
    try {
      // Get current language from i18n
      const currentLanguage = i18n?.language || 'en';
      const languageParam = currentLanguage.startsWith('nl') ? 'nl' : 'en';
      const response = await axios.get(`/api/salesDocuments/${id}/download?language=${languageParam}`);
      const { fileUrl } = response.data;

      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        fileUrl
      )}&embedded=true`;
      window.open(viewerUrl, "_blank");
    } catch (error) {
      console.error("Error viewing the document:", error);
    }
  };

  const handleDownload = async (id) => {
    try {
      // Get current language from i18n
      const currentLanguage = i18n?.language || 'en';
      const languageParam = currentLanguage.startsWith('nl') ? 'nl' : 'en';
      const response = await axios.get(`/api/salesDocuments/${id}/download?language=${languageParam}`);
      const { fileUrl } = response.data;
      console.log(fileUrl);
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("Error downloading the document:", error);
    }
  };

  const handleSendMessage = async (id) => {
    try {
      // Step 1: Get the pre-signed URL for the document
      const response = await axios.get(`/api/salesDocuments/${id}/download`);
      const { fileUrl } = response.data;

      // Step 2: Fetch the file using the pre-signed URL
      const fileResponse = await axios.get(fileUrl, { responseType: "blob" });

      const doc = salesDocuments.find((d) => d._id === id);
      const fileName = doc?.fileName || doc?.name || `${id}.docx`;
      const file = new File([fileResponse.data], fileName, {
        type: fileResponse.data.type,
      });

      // const file = new File([fileResponse.data], `${id}.docx`, {
      //   type: fileResponse.data.type,
      // });

      // Step 3: Convert the file to base64
      const base64Content = await convertFileToBase64(file);

      // Step 4: Navigate to the SendEmail page with the file as an attachment
      navigate("/send-email", {
        state: { attachments: [{ file, base64Content, name: file.name }] },
      });
    } catch (error) {
      console.error("Error fetching the document:", error);
    }
  };

  // const handleSendMessage = async(id) => {
  //   console.log("button is clicking")
  //   try {
  //     // Step 1: Get the pre-signed URL for the document
  //     const response = await axios.get(`/api/salesDocuments/${id}/download`);
  //     const { fileUrl } = response.data;

  //     // Step 2: Fetch the file using the pre-signed URL
  //     const fileResponse = await axios.get(fileUrl, { responseType: "blob" });
  //     // Try to get the original file name from your salesDocuments list
  //     const doc = salesDocuments.find((d) => d._id === id);
  //     const fileName = doc?.fileName || doc?.name || `${id}.docx`;
  //     const file = new File([fileResponse.data], fileName, {
  //       type: fileResponse.data.type,
  //     });

  //     // Step 3: Convert the file to base64
  //     const base64Content = await convertFileToBase64(file);

  //     // Step 4: Navigate to the SendEmail page with the file as an attachment
  //     navigate("/send-email", {
  //       state: { attachments: [{ file, base64Content, name: file.name, type: file.type }] },
  //     });
  //   } catch (error) {
  //     console.error("Error fetching the document:", error);
  //   }
  // };


  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedSalesDocuments = [...filteredSalesDocuments].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "from") {
      aValue = a.from
        ? `${a.from.firstName} ${a.from.middleName || ""} ${a.from.lastName}`.trim()
        : "";
      bValue = b.from
        ? `${b.from.firstName} ${b.from.middleName || ""} ${b.from.lastName}`.trim()
        : "";
    } else if (orderBy === "to") {
      aValue = a.to
        ? `${a.to.firstName} ${a.to.middleName || ""} ${a.to.lastName}`.trim()
        : "";
      bValue = b.to
        ? `${b.to.firstName} ${b.to.middleName || ""} ${b.to.lastName}`.trim()
        : "";
    } else if (orderBy === "case") {
      aValue = a.case ? a.case.name : "";
      bValue = b.case ? b.case.name : "";
    } else if (orderBy === "createdAt") {
      // Sort by creation date/time (newest first by default)
      const aDate = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const bDate = b.createdAt ? new Date(b.createdAt) : new Date(0);
      
      if (order === "asc") {
        return aDate - bDate;
      } else {
        return bDate - aDate;
      }
    } else if (orderBy === "dueDate") {
      // Sort by due date (date comparison)
      // Handle null/undefined dates by putting them at the end
      const aDate = a.dueDate ? new Date(a.dueDate) : null;
      const bDate = b.dueDate ? new Date(b.dueDate) : null;
      
      // If one date is null, put it at the end
      if (!aDate && !bDate) {
        // Both null - sort by creation time (newest first)
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      if (!aDate) return 1; // a goes to end
      if (!bDate) return -1; // b goes to end
      
      if (order === "asc") {
        // Ascending: earliest due dates first
        // If due dates are the same, sort by creation time (newest first)
        if (aDate.getTime() === bDate.getTime()) {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        return aDate - bDate;
      } else {
        // Descending: latest due dates first
        // If due dates are the same, sort by creation time (newest first)
        if (aDate.getTime() === bDate.getTime()) {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        return bDate - aDate;
      }
    } else if (orderBy === "totalDue" || orderBy === "total") {
      // Sort by numeric values
      aValue = Number(a[orderBy] || 0);
      bValue = Number(b[orderBy] || 0);
      
      if (order === "asc") {
        // If values are the same, sort by creation time (newest first)
        if (aValue === bValue) {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        return aValue - bValue;
      } else {
        // If values are the same, sort by creation time (newest first)
        if (aValue === bValue) {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        return bValue - aValue;
      }
    }

    // For string comparisons
    if (order === "asc") {
      // If values are the same, sort by creation time (newest first)
      if (aValue === bValue) {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      return aValue < bValue ? -1 : 1;
    } else {
      // If values are the same, sort by creation time (newest first)
      if (aValue === bValue) {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      return aValue > bValue ? -1 : 1;
    }
  });

  const sortedSalesPayments = [...filteredSalesPayments].sort((a, b) => {
    // Default: newest first by creation time
    if (!orderBy) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }

    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "from") {
      aValue = a.from
        ? `${a.from.firstName} ${a.from.middleName || ""} ${a.from.lastName}`.trim()
        : "";
      bValue = b.from
        ? `${b.from.firstName} ${b.from.middleName || ""} ${b.from.lastName}`.trim()
        : "";
    } else if (orderBy === "to") {
      aValue = a.to
        ? `${a.to.firstName} ${a.to.middleName || ""} ${a.to.lastName}`.trim()
        : "";
      bValue = b.to
        ? `${b.to.firstName} ${b.to.middleName || ""} ${b.to.lastName}`.trim()
        : "";
    } else if (orderBy === "case") {
      aValue = a.case ? a.case.name : "";
      bValue = b.case ? b.case.name : "";
    }

    if (order === "asc") {
      return aValue < bValue ? -1 : 1;
    } else {
      return aValue > bValue ? -1 : 1;
    }
  });

  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "#85bc20"; // Green color for Paid status
      case "unpaid":
        return "#e5202e"; // Red color for Unpaid status
      case "partially paid":
        return "orange"; // Orange color for Partially Paid status
      default:
        return "gray"; // Default color in case of unknown status
    }
  };


  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", // Reduced shadow spread
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:finance.title')} />

        {/* <Box sx={{ textAlign: "center" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateRangePicker
              startText="Start Date"
              endText="End Date"
              size="small"
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                textField: {
                  InputProps: { endAdornment: <Calendar /> },
                  fullWidth: true,
                  sx: textFieldStyles({ width: "100%" }),
                  size: "small",
                  label: "Select Billing Period",
                },
              }}
              value={billingPeriod}
              onOpen={() => setIsPickerOpen(true)}
              onClose={() => setIsPickerOpen(false)}
              onChange={(newValue) => setBillingPeriod(newValue)}
            />
          </LocalizationProvider>
        </Box> */}
        <div>
          <Button
            variant="text"
            onClick={handleOpen}
            sx={{
              textTransform: "none", mr: 2, ...smallBold,
              color: "#2B80B6",
              fontWeight: 400,
              fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",

            }}
          >
            {t('pages:finance.register_payments')}
          </Button>
          <Button
            sx={actionButtonStyles}
            onClick={handleMenuOpen}
            endIcon={<ArrowDropDownIcon />}
            startIcon={
              <Box display="flex" alignItems="center">
                <AddIcon />
                <Box ml={1} />
              </Box>
            }
          >
            {t('buttons:primary.new_document')}
          </Button>
          <Menu
            PaperProps={{
              style: {
                width: anchorEl ? anchorEl.clientWidth : undefined,
                boxShadow: "0px 3px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "0",
              },
            }}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MuiMenuItem
              onClick={() => handleMenuItemClick("/finance/create-invoice")}
            >
              {t('pages:finance.invoice')}
            </MuiMenuItem>
            <MuiMenuItem
              onClick={() => handleMenuItemClick("/finance/create-credit")}
            >
              {t('pages:finance.credit')}
            </MuiMenuItem>
            <MuiMenuItem
              onClick={() => handleMenuItemClick("/finance/create-provision")}
            >
              {t('pages:finance.provision_note')}
            </MuiMenuItem>
          </Menu>

          <SalesPaymentPopup open={open} onClose={handleClose} />
        </div>
        {/* <Button
          startIcon={<AddIcon />}
          onClick={() => navigate("/timesheets/create")}
          size="small"
          sx={actionButtonStyles()}
        >
          Create Timesheet
        </Button> */}
      </Box>

      <SalesDocumentStats 
        fromFilter={fromFilter}
        toFilter={toFilter}
        caseFilter={caseFilter}
        statusFilter={statusFilter}
        fromFilterCleared={fromFilterCleared}
        billingPeriod={billingPeriod}
      />
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="finance tabs"
        indicatorColor="#2B80B6"
        sx={{
          minHeight: "32px",
          height: "32px",
          "& .MuiTab-root": {
            ...typoBase,
            color: "black",
            minHeight: "32px",
            height: "32px",
            textTransform: "capitalize", // Capitalize first letter
            fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
            fontWeight: 400,
            fontSize: "11.5px",
          },
          "& .Mui-selected": {
            ...smallBold,
            color: "#2B80B6",
            borderBottom: "1px solid #2B80B6",
            fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
            fontWeight: 400,
            fontSize: "11.5px",
          },
        }}
      >
        <Tab label={t('pages:finance.sales_documents')} />
        <Tab label={t('pages:finance.sales_payments')} />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Grid
            container
            spacing={3}
            alignItems="center"
            style={{ marginTop: 2 }}
          >
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginBottom: 2,
                }}
              >
                <TextField
                  label={t('common:search')}
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    sx: {
                      // further adjustments to inner padding
                      padding: "0 5px",
                    },
                  }}
                  sx={textFieldStyles({ width: "20%", marginRight: "0" })}
                />
                <FormControl
                  variant="outlined"
                  sx={{ width: "20%" }}
                  size="small"
                >
                  <CustomAutocomplete
                    options={relationships?.filter(
                      (rel) => rel.type === "Lawyer"
                    )}
                    value={fromFilter}
                    onChange={(event, newValue) => {
                      setFromFilter(newValue);
                      // Track if filter was manually cleared
                      if (newValue === null) {
                        setFromFilterCleared(true);
                      } else {
                        setFromFilterCleared(false);
                      }
                    }}
                    label={t('common:filter_by_from')}
                    filterType="Member"
                    addNewLabel={t('common:add_owner')}
                    addNewPath="/relationships/create"
                    showIcon={true} // Show the icon
                    icon={FilterListIcon} // Pass the icon you want to display
                    showAddNewOption={false}
                  />
                </FormControl>
                <FormControl
                  variant="outlined"
                  sx={{ width: "20%" }}
                  size="small"
                >
                  <CustomAutocomplete
                    options={relationships?.filter(
                      (rel) => rel.type !== "Lawyer"
                    )}
                    value={toFilter}
                    onChange={(event, newValue) => setToFilter(newValue)}
                    label={t('common:filter_by_to')}
                    filterType="Party"
                    addNewLabel={t('common:add_owner')}
                    addNewPath="/relationships/create"
                    showIcon={true} // Show the icon
                    icon={FilterListIcon} // Pass the icon you want to display
                    showAddNewOption={false}
                  />
                </FormControl>
                <FileAutocomplete
                  options={userFiles}
                  value={caseFilter}
                  onChange={(event, newValue) => setCaseFilter(newValue)}
                  label={t('common:case')}
                  name="case"
                  size="small"
                  icon={<FilterListIcon />}
                  width="20%"
                />

                <Autocomplete
                  options={[
                    { value: "draft", label: t('common:draft') },
                    { value: "unpaid", label: t('common:unpaid') },
                    { value: "paid", label: t('common:paid') }
                  ]}
                  value={statusFilter ? { value: statusFilter, label: t(`common:${statusFilter}`) } : null}
                  sx={{ width: "20%" }}
                  size="small"
                  getOptionLabel={(option) => option.label || ""}
                  onChange={(event, newValue) => setStatusFilter(newValue?.value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common:filter_by_status')}
                      sx={textFieldStyles({ marginRight: "0" })}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterListIcon />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          <Table 
            sx={tableStyles.container}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>{t('common:number')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t('common:invoice_number')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell }}>
                  <TableSortLabel
                    active={orderBy === "type"}
                    direction={orderBy === "type" ? order : "asc"}
                    onClick={() => handleRequestSort("type")}
                  >
                    {t('common:type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "from"}
                    direction={orderBy === "from" ? order : "asc"}
                    onClick={() => handleRequestSort("from")}
                  >
                    {t('common:from')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "to"}
                    direction={orderBy === "to" ? order : "asc"}
                    onClick={() => handleRequestSort("to")}
                  >
                    {t('common:to')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: "90px" }}>
                  <TableSortLabel
                    active={orderBy === "dueDate"}
                    direction={orderBy === "dueDate" ? order : "asc"}
                    onClick={() => handleRequestSort("dueDate")}
                  >
                    {t('common:due_date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: "170px" }}>
                  <TableSortLabel
                    active={orderBy === "case"}
                    direction={orderBy === "case" ? order : "asc"}
                    onClick={() => handleRequestSort("case")}
                  >
                    {t('common:case')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleRequestSort("status")}
                  >
                    {t('common:status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: "90px" }}>
                  <TableSortLabel
                    active={orderBy === "totalDue"}
                    direction={orderBy === "totalDue" ? order : "asc"}
                    onClick={() => handleRequestSort("totalDue")}
                  >
                    {t('pages:finance.total_due')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "total"}
                    direction={orderBy === "total" ? order : "asc"}
                    onClick={() => handleRequestSort("total")}
                  >
                    {t('forms:labels.amount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('common:actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSalesDocuments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((doc, index) => (
                  <TableRow key={doc._id} sx={{ ...tableStyles.row }}>
                    <TableCell sx={tableStyles.cell}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={tableStyles.cell}>
                      {doc.sequenceNumber || doc._id.slice(-6)}
                    </TableCell>
                    <TableCell sx={tableStyles.cell}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InsertDriveFileIcon
                          sx={{
                            fontSize: 13,
                            color:
                              doc.type === "invoice"
                                ? "rgb(133, 188, 32) !important"
                                : doc.type === "credit"
                                  ? "rgb(66, 94, 16) !important"
                                  : "rgb(114, 16, 23) !important",
                            marginRight: "-2.5px"
                          }}
                        />
                        <span
                          onClick={() => {
                            if (doc.type === 'invoice') {
                              navigate(`/invoice/${doc._id}`);
                            } else if (doc.type === 'credit') {
                              navigate(`/credit/${doc._id}`);
                            } else if (doc.type === 'provision note') {
                              navigate(`/provision/${doc._id}`);
                            } else {
                              navigate(`/default/${doc._id}`); // Fallback for unknown types
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          {truncateText(t(`pages:finance.${doc.type}`) || doc.type, 20)}
                        </span>
                      </Box>
                    </TableCell>


                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${doc?.from?._id}`); }}>
                      {doc.from ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            alt={doc.from.firstName}
                            src={
                              doc.from.avatar
                                ? doc.from.avatar
                                : "/static/images/avatar/1.jpg" // Default avatar if none is provided
                            }
                            sx={{
                              width: "1rem", // Set width to 1rem
                              height: "1rem", // Set height to 1rem
                              fontSize: "7.8px", // Set font size to 8px

                            }}
                          >
                            {/* Display initials if no avatar */}
                            {!doc.from.avatar &&
                              doc.from.firstName &&
                              doc.from.lastName
                              ? doc.from.firstName.charAt(0) + doc.from.lastName.charAt(0) // First and last initial
                              : !doc.from.avatar && doc.from.firstName
                                ? doc.from.firstName.charAt(0) // Only first initial
                                : !doc.from.avatar && doc.from.lastName
                                  ? doc.from.lastName.charAt(0) // Only last initial
                                  : ""}
                          </Avatar>
                          <span>
                            {`${doc.from.firstName || ""} ${doc.from.middleName || ""} ${doc.from.lastName || ""}`.trim()}
                          </span>
                        </Box>
                      ) : (
                        "                        t('common:no_from')"
                      )}
                    </TableCell>



                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${doc?.to?._id}`); }}>
                      {doc.to ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            alt={
                              doc.to.contractType !== "Individual"
                                ? doc.to.businessName
                                : `${doc.to.firstName} ${doc.to.lastName}`
                            }
                            src={
                              doc.to.avatar
                                ? doc.to.avatar
                                : "/static/images/avatar/1.jpg" // Default avatar if none is provided
                            }
                            sx={{
                              width: "1rem", // Set width to 1rem
                              height: "1rem", // Set height to 1rem
                              fontSize: "7.8px", // Set font size to 8px

                            }}
                          >
                            {/* Display initials if no avatar */}
                            {!doc.to.avatar &&
                              doc.to.firstName &&
                              doc.to.lastName
                              ? doc.to.firstName.charAt(0) + doc.to.lastName.charAt(0) // First and last initial
                              : !doc.to.avatar && doc.to.firstName
                                ? doc.to.firstName.charAt(0) // Only first initial
                                : !doc.to.avatar && doc.to.lastName
                                  ? doc.to.lastName.charAt(0) // Only last initial
                                  : ""}
                          </Avatar>
                          <span>
                            {doc.to.contractType !== "Individual"
                              ? `${doc.to.businessName || ""}`
                              : `${doc.to.firstName || ""} ${doc.to.middleName ? doc.to.middleName + " " : ""}${doc.to.lastName || ""}`}
                          </span>
                        </Box>
                      ) : (
                        "                        t('common:no_to')"
                      )}
                    </TableCell>



                    <TableCell sx={tableStyles.cell}>
                      {formatDate(doc.dueDate)}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { cursor: 'pointer', textDecoration: "underline" }, color: "#005b92", }}
                      onClick={() => navigate(`/files/${doc?.case?._id
                        }/overview`)}>
                      {doc.case ? (
                        <>
                          <Stack direction="row">
                            <Typography
                              variant="caption"

                              sx={{ fontSize: "0.7rem" }} // Smaller font size
                            >
                              {doc.case.fileNo} {truncateText(doc.case.name, 25)}
                            </Typography>
                            
                          </Stack>
                        </>
                      ) : (
                        "                        t('common:no_case')"
                      )}
                    </TableCell>
                    <TableCell sx={tableStyles.cell}>
                      <span
                        style={{
                          padding: "1px 6px",
                          borderRadius: "20px", // To make it rounded
                          backgroundColor: getStatusColor(doc.status), // Dynamically assign color
                          color: doc?.status === "paid" ? "black" : "white", // White text color 
                          fontSize: "8.25px",
                          fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                          fontWeight: "700",
                          textTransform: "upperCase",
                          height: "12px",
                          letterSpacing: "0.2em"
                        }}
                      >
                        {t(`common:${doc.status}`) || doc.status}
                      </span>
                    </TableCell>

                    <TableCell sx={tableStyles.cell}>{doc.totalDue.toFixed(2)}</TableCell>
                    <TableCell sx={tableStyles.cell}>{doc.total.toFixed(2)}</TableCell>
                    <TableCell sx={tableStyles.cell}>
                      <Box display="flex">
                        <Tooltip title={t('common:view_document')}>
                          <IconButton
                            onClick={() => handleViewDocument(doc._id)}
                            sx={{ width: "20px", height: "20px", marginX: "8px", color: "#005b92", }}
                          >
                            <VisibilityIcon sx={{ fontSize: "20px" }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('common:download')}>
                          <IconButton onClick={() => handleDownload(doc._id)} sx={{ width: "20px", height: "20px", marginX: "8px", color: "#005b92", }}>
                            <DownloadIcon sx={{ fontSize: "20px" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:send_email')}>
                          <IconButton
                            onClick={() => handleSendMessage(doc._id)}
                            sx={{ width: "20px", height: "20px", marginX: "8px", color: "#005b92", }} disabled={false}
                          >
                            <SendIcon sx={{ fontSize: "20px" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={8} sx={{...tableStyles.cell, fontWeight: "bold", fontSize: "12px"}}>
                    {t('Total') || 'Total'}
                  </TableCell>
                  <TableCell sx={{...tableStyles.cell, fontWeight: "bold", padding: "2px 8px"}}>
                    {calculateTotalDue(filteredSalesDocuments)}
                  </TableCell>
                  <TableCell sx={{...tableStyles.cell, fontWeight: "bold", padding: "2px 8px"}}>
                    {calculateTotalAmount(filteredSalesDocuments)}
                  </TableCell>
                  <TableCell sx={tableStyles.cell}></TableCell>
                </TableRow>
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredSalesDocuments.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <Grid
            container
            spacing={3}
            alignItems="center"
            style={{ marginTop: 2 }}
          >
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginBottom: 2,
                }}
              >
                <TextField
                  label={t('common:search')}
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    sx: {
                      // further adjustments to inner padding
                      padding: "0 5px",
                    },
                  }}
                  sx={textFieldStyles({ width: "25%", marginRight: "0" })}
                />
                <FormControl
                  variant="outlined"
                  sx={{ width: "25%" }}
                  size="small"
                >
                  <CustomAutocomplete
                    options={relationships?.filter(
                      (rel) => rel.type === "Lawyer"
                    )}
                    value={fromFilter}
                    onChange={(event, newValue) => {
                      setFromFilter(newValue);
                      // Track if filter was manually cleared
                      if (newValue === null) {
                        setFromFilterCleared(true);
                      } else {
                        setFromFilterCleared(false);
                      }
                    }}
                    label={t('common:filter_by_from')}
                    filterType="Member"
                    addNewLabel={t('common:add_owner')}
                    addNewPath="/relationships/create"
                    showIcon={true} // Show the icon
                    icon={FilterListIcon} // Pass the icon you want to display
                    showAddNewOption={false}
                  />
                </FormControl>
                <FormControl
                  variant="outlined"
                  sx={{ width: "25%" }}
                  size="small"
                >
                  <CustomAutocomplete
                    options={relationships?.filter(
                      (rel) => rel.type !== "Lawyer"
                    )}
                    value={toFilter}
                    onChange={(event, newValue) => setToFilter(newValue)}
                    label={t('common:filter_by_to')}
                    filterType="Party"
                    addNewLabel={t('common:add_owner')}
                    addNewPath="/relationships/create"
                    showIcon={true} // Show the icon
                    icon={FilterListIcon} // Pass the icon you want to display
                    showAddNewOption={false}
                  />
                </FormControl>
                <FileAutocomplete
                  options={userFiles}
                  value={caseFilter}
                  onChange={(event, newValue) => setCaseFilter(newValue)}
                  label={t('common:case')}
                  name="case"
                  icon={<FilterListIcon />}
                  width="25%"
                />
                {/* <Autocomplete
                  options={files}
                  getOptionLabel={(option) => option.name}
                  value={caseFilter}
                  onChange={(event, newValue) => setCaseFilter(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Case"
                      variant="standard"
                      sx={{ width: 200 }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterListIcon />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                /> */}
                {/* <Autocomplete
                  options={['draft', 'unpaid', 'paid']}
                  value={statusFilter}
                  onChange={(event, newValue) => setStatusFilter(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Status"
                      variant="standard"
                      sx={{ width: 200 }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterListIcon />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                /> */}
              </Box>
            </Grid>
          </Grid>

          <Table 
            sx={tableStyles.container}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>{t('common:number')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "type"}
                    direction={orderBy === "type" ? order : "asc"}
                    onClick={() => handleRequestSort("type")}
                  >
                    {t('common:type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "salesDocument.type"}
                    direction={orderBy === "salesDocument.type" ? order : "asc"}
                    onClick={() => handleRequestSort("salesDocument.type")}
                  >
                    {t('common:doc_type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "from"}
                    direction={orderBy === "from" ? order : "asc"}
                    onClick={() => handleRequestSort("from")}
                  >
                    {t('common:from')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "to"}
                    direction={orderBy === "to" ? order : "asc"}
                    onClick={() => handleRequestSort("to")}
                  >
                    {t('common:to')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "date"}
                    direction={orderBy === "date" ? order : "asc"}
                    onClick={() => handleRequestSort("date")}
                  >
                    {t('common:date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "case"}
                    direction={orderBy === "case" ? order : "asc"}
                    onClick={() => handleRequestSort("case")}
                  >
                    {t('common:case_file')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "amount"}
                    direction={orderBy === "amount" ? order : "asc"}
                    onClick={() => handleRequestSort("amount")}
                  >
                    {t('common:amount')}
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "salesDocument._id"}
                    direction={orderBy === "salesDocument._id" ? order : "asc"}
                    onClick={() => handleRequestSort("salesDocument._id")}
                  >
                    {t('common:doc_id')}
                  </TableSortLabel>
                </TableCell> */}
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "salesDocumentTotal"}
                    direction={orderBy === "salesDocumentTotal" ? order : "asc"}
                    onClick={() => handleRequestSort("salesDocumentTotal")}
                  >
                    {t('common:doc_total')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSalesPayments
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((payment, index) => (
                  <TableRow key={payment?._id} sx={{ ...tableStyles.row, }}>
                    <TableCell sx={tableStyles.cell}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={tableStyles.cell}>{truncateText(payment?.type, 20)}</TableCell>
                    <TableCell sx={tableStyles.cell}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InsertDriveFileIcon
                          sx={{
                            fontSize: 12,
                            color:
                              payment?.salesDocument?.type === "invoice"
                                ? "rgb(133, 188, 32) !important"
                                : payment?.salesDocument?.type === "credit"
                                  ? "rgb(66, 94, 16) !important"
                                  : "rgb(114, 16, 23) !important",
                            marginRight: "-2.5px"
                          }}
                        />
                        <span
                          onClick={() => payment.salesDocument.type === 'invoice' && navigate(`/invoice/${payment.salesDocument._id}`)}
                          style={{
                            cursor: payment.salesDocument.type === 'invoice' ? 'pointer' : 'default',
                            textDecoration: payment.salesDocument.type === 'invoice' ? 'underline' : 'none'
                          }}
                        >
                          {truncateText(payment.salesDocument.type, 20)}
                        </span>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${payment?.from?._id}`); }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          alt={`${payment?.from.firstName} ${payment?.from.lastName}`}
                          src={payment?.from.avatar ? payment?.from.avatar : "/static/images/avatar/1.jpg"}
                          sx={{
                            width: "1rem", // Set width to 1rem
                            height: "1rem", // Set height to 1rem
                            fontSize: "8px", // Set font size to 8px
                            bgcolor: !payment.from.avatar
                              ? stringToColor(`${payment.from.firstName} ${payment.from.lastName}`)
                              : "transparent", // Random color if no avatar
                          }}
                        >
                          {/* Display initials if no avatar */}
                          {!payment.from.avatar &&
                            payment.from.firstName &&
                            payment.from.lastName
                            ? payment.from.firstName.charAt(0) + payment.from.lastName.charAt(0) // First and last initial
                            : !payment.from.avatar && payment.from.firstName
                              ? payment.from.firstName.charAt(0) // Only first initial
                              : !payment.from.avatar && payment.from.lastName
                                ? payment.from.lastName.charAt(0) // Only last initial
                                : ""}
                        </Avatar>
                        {/* Display Name */}
                        <span>
                          {payment.from
                            ? `${payment.from.firstName} ${payment.from.middleName ? payment.from.middleName + " " : ""}${payment.from.lastName}`
                            : t('common:no_from')}
                        </span>
                      </Box>
                    </TableCell>



                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${payment?.to?._id}`); }}>
                      {payment.to ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            alt={
                              payment.to.contractType !== "Individual"
                                ? payment.to.businessName
                                : `${payment.to.firstName} ${payment.to.lastName}`
                            }
                            src={
                              payment.to.avatar ? payment.to.avatar : "/static/images/avatar/1.jpg" // Default avatar if none is provided
                            }
                            sx={{
                              width: "1rem", // Set width to 1rem
                              height: "1rem", // Set height to 1rem
                              fontSize: "8px", // Set font size to 8px
                              backgroundColor: stringToColor(
                                payment.to.contractType !== "Individual"
                                  ? payment.to.businessName
                                  : `${payment.to.firstName} ${payment.to.lastName}`
                              ), // Use random color function for background
                            }}
                          >
                            {!payment.to.avatar &&
                              payment.to.firstName &&
                              payment.to.lastName
                              ? payment.to.firstName.charAt(0) + payment.to.lastName.charAt(0) // Show both initials
                              : !payment.to.avatar && payment.to.firstName
                                ? payment.to.firstName.charAt(0) // Show first initial if no avatar
                                : !payment.to.avatar && payment.to.lastName
                                  ? payment.to.lastName.charAt(0) // Show last initial if no avatar
                                  : ""}
                          </Avatar>
                          <span>
                            {payment.to.contractType !== "Individual"
                              ? payment.to.businessName || ""
                              : `${payment.to.firstName || ""} ${payment.to.middleName ? payment.to.middleName + " " : ""}${payment.to.lastName || ""}`}
                          </span>
                        </Box>
                      ) : (
                        "                        t('common:no_to')"
                      )}
                    </TableCell>




                    <TableCell sx={tableStyles.cell}>
                      {formatDate(payment.date)}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { cursor: 'pointer', textDecoration: "underline" }, color: "#005b92", }}
                      onClick={() => navigate(`/files/${payment?.case?._id
                        }/overview`)}>
                      {payment.case ? (
                        <>
                          <Stack direction="row">
                            <Typography
                              variant="caption"

                              sx={{ fontSize: "0.7rem" }} // Smaller font size
                            >
                              {payment.case.fileNo} {truncateText(payment.case.name, 30)}
                            </Typography>
                            
                          </Stack>
                        </>
                      ) : (
                        "                        t('common:no_case')"
                      )}
                    </TableCell>
                    <TableCell sx={tableStyles.cell}>{payment.amount.toFixed(2)}</TableCell>
                    {/* <TableCell sx={tableStyles.cell}>{payment.salesDocument._id}</TableCell> */}
                    <TableCell sx={tableStyles.cell}>
                      {payment.salesDocumentTotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredSalesPayments.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage={t('common:rows_per_page')}
          />
        </>
      )}
    </Container>
  );
};

export default FinancePage;
